const MB_CLUB_MINICART_INTELLIGEMS_EXPERIMENT_ID = window.theme?.mb_club_intelligems_experiment_id || 'cae51465-22e0-4cfd-9789-15a4a98cd626';

let mbClubMinicartIntelligemsShouldRenderPromise = null;
let mbClubSubscriptionAddInFlight = false;

// Tracks the last visibility value pushed to the dataLayer so we only push on change.
let mbClubLastReportedWidgetVisible = null;

/**
 * Pushes the member widget visibility to the GTM dataLayer as `member_widget_visible`.
 * Only pushes when the value changes to avoid spamming the dataLayer on every cart re-render.
 * @param {boolean} isVisible - Whether the MB Club / VIP membership widget is currently shown.
 */
function mbClubReportWidgetVisible(isVisible) {
  const value = !!isVisible;
  if (mbClubLastReportedWidgetVisible === value) return;
  mbClubLastReportedWidgetVisible = value;
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ member_widget_visible: value });
  } catch (error) {
    console.warn('[MB Club Minicart] Failed to push member_widget_visible:', error);
  }
}
// Exposed (when this widget is the one defined, see the end of the file) so other cart scripts
// (e.g. side-cart.js empty-cart path) share the same dedupe state.

/**
 * Returns the visitor's current Intelligems experiment assignments, keyed by experiment id.
 * Returns an empty object if Intelligems isn't loaded/ready, so it never blocks add to cart.
 */
function mbClubGetIntelligemsAssignments() {
  try {
    const user = window.igData?.user;
    if (!user?.getTestGroup) return {};
    const toAssignment = (experimentName, testGroup) => ({
      experiment: experimentName,
      group: testGroup?.name ?? '(no assignment)',
      testGroupId: testGroup?.id ?? null,
    });
    const assignments = Object.fromEntries(
      (user.getExperiments?.() ?? []).map(experiment => [
        experiment.id,
        toAssignment(experiment.name, user.getTestGroup(experiment.id)),
      ])
    );
    // getExperiments() only returns experiments targeted to the current page, but the
    // membership can be added from any page, so the MB Club experiments are read directly.
    [MB_CLUB_MINICART_INTELLIGEMS_EXPERIMENT_ID, window.mbClubExperiment?.experimentId].filter(Boolean).forEach(experimentId => {
      if (assignments[experimentId]) return;
      const mbClubTestGroup = user.getTestGroup(experimentId);
      if (mbClubTestGroup) {
        assignments[experimentId] = toAssignment(null, mbClubTestGroup);
      }
    });
    return assignments;
  } catch (error) {
    console.warn('[MB Club Minicart] Failed to read Intelligems assignments:', error);
    return {};
  }
}

/**
 * Returns whether the MB Club minicart widget should render for this visitor.
 * Control (and "unknown"/unassigned) => do nothing (no render).
 * Variant => render as normal.
 */
function mbClubMinicartShouldRenderFromIntelligems() {
  if (mbClubMinicartIntelligemsShouldRenderPromise) return mbClubMinicartIntelligemsShouldRenderPromise;

  mbClubMinicartIntelligemsShouldRenderPromise = new Promise((resolve) => {
    const decide = () => {
      try {
        const testObject = window?.igData?.user?.getTestGroup?.(MB_CLUB_MINICART_INTELLIGEMS_EXPERIMENT_ID);
        if (!testObject) {
          console.log('[mb-club-minicart] Intelligems test not found for experiment', MB_CLUB_MINICART_INTELLIGEMS_EXPERIMENT_ID, '- treating as no render');
          return resolve(false);
        }
        return resolve(!testObject.isControl);
      } catch (error) {
        console.warn('[MB Club Minicart] Intelligems evaluation failed:', error);
        return resolve(false);
      }
    };

    // If Intelligems is already ready, decide immediately.
    if (window?.igData?.user?.getTestGroup) {
      decide();
      return;
    }

    // Otherwise wait for Intelligems readiness event.
    window.addEventListener('ig:ready', decide, { once: true });
  });

  return mbClubMinicartIntelligemsShouldRenderPromise;
}


class MBClubMinicart extends HTMLElement {
  constructor() {
    super();
    // Check if setting is "true" (string from Liquid checkbox) or default to true
    const settingValue = window.theme?.mb_club_default_open;
    const localStorageOpen = localStorage.getItem('mb_club_minicart_open');
    this.isOpen = (settingValue === "true" && !localStorageOpen) || localStorageOpen === "true" || settingValue === "" || settingValue === undefined;
    this.globalThisVariables();
  } 
  globalThisVariables() {
    this.exclusiveCountries = window.theme?.mb_club_exclusive_countries;
    this.mbClubSubscriptionProductHandle = window.theme?.mb_club_subscription_product_handle;
    this.mbClubSubscriptionProductId = window.theme?.mb_club_subscription_product_id;
    this.mbClubSubscriptionProductVariantId = window.theme?.mb_club_subscription_product_variant_id;
    this.mbClubSubscriptionProductSellingPlanId = window.theme?.mb_club_subscription_product_selling_plan_id;
    this.mbClubSubscriptionProductDefaultSelected = window.theme?.mb_club_subscription_product_default_selected;
    this.mbClubDiscountPercent = window.theme?.mb_club_discount_percent;
    this.renderedFromLiquid = this.getAttribute('rendered-from-liquid') === 'true';
    this.isSubscriptionAdded = this.getAttribute('is-subscription-added') === 'true'; // true or false
    this.mbClubSubscriptionExcludeProducts = window.theme?.mb_club_subscription_exclude_products;
    this.mbClubSubscriptionProductPrice = window.theme?.mb_club_subscription_product_price;
    this.mbClubSubscriptionAllowedProductTypes = window.theme?.mb_club_subscription_allowed_product_types;
    // Visitors assigned in the M&B Club A/B test (mb-club-experiment.js) already got this widget from it,
    // so the older show/hide experiment is skipped for them.
    this.mbClubIntelligemsEnabled = window.theme?.mb_club_intelligems_enabled !== false && !window.mbClubExperiment?.variant?.assigned;
  }
  setVipMemberStatus(status, meta = {}) {
    try {
      localStorage.setItem('mb-vip-member-status', JSON.stringify({
        status,
        ...meta,
        updatedAt: Date.now()
      }));
      console.log('[mb-club-minicart] mb-vip-member-status ->', status, meta);
    } catch (error) {
      console.warn('[MB Club Minicart] Failed to save vip member status to localStorage:', error);
    }
    // Fire the trigger in the same tick the status is decided, so a GTM tag reading
    // localStorage off this event always sees the value we just wrote (no race with
    // the async Intelligems check, which used to be handled via a fixed setTimeout).
    console.log('[mb-club-minicart] widget_membership_trigger firing. localStorage snapshot:', {
      'vip-ig-experiment': localStorage.getItem('vip-ig-experiment'),
      'mb-vip-member-status': localStorage.getItem('mb-vip-member-status'),
    });
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'event': 'widget_membership_trigger' });
  }
  /**
   * Reports the widget's actual visibility (connected to the DOM and not hidden) to the dataLayer.
   */
  reportVisibility() {
    mbClubReportWidgetVisible(this.isConnected && !this.classList.contains('hidden'));
  }
  saveIntelligemsExperimentToLocalStorage(shouldRender) {
    try {
      const variant = shouldRender ? 'variant' : 'control';
      localStorage.setItem('vip-ig-experiment', JSON.stringify({
        experimentId: MB_CLUB_MINICART_INTELLIGEMS_EXPERIMENT_ID,
        variant
      }));
      console.log('[mb-club-minicart] vip-ig-experiment written to localStorage at +' + performance.now().toFixed(0) + 'ms since page load, variant:', variant);
    } catch (error) {
      console.warn('[MB Club Minicart] Failed to save Intelligems experiment to localStorage:', error);
    }
  }
  addSubscriptionProductToCart(from = false) {
    return new Promise((resolve, reject) => {
      const sideCart = document.querySelector('side-cart');
      try {
        const properties = {
          _added_from_mini_cart: true,
          _hide_on_cart: true
        };
        // If Intelligems isn't ready yet (e.g. A/B test disabled from theme settings),
        // the property is omitted rather than delaying the add to cart.
        const igAssignments = mbClubGetIntelligemsAssignments();
        if (Object.keys(igAssignments).length) {
          // Line item property values must be strings, so the assignments are stored as JSON.
          properties._ig_experiments = JSON.stringify(igAssignments);
        } else {
          console.warn('[MB Club Minicart] No Intelligems assignments found, _ig_experiments omitted');
        }
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: this.mbClubSubscriptionProductVariantId,
            quantity: 1,
            selling_plan: this.mbClubSubscriptionProductSellingPlanId,
            properties
          })
        })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then(data => {
          this.setAttribute('is-subscription-added', 'true');
          localStorage.setItem('mb_club_minicart_disabled', 'false');
          this.isSubscriptionAdded = true;
          resolve(data);
        })
        .catch(error => {
          console.error('Error adding subscription product to cart:', error);
          reject(error);
        });
      } catch (error) {
        console.error('Error in addSubscriptionProductToCart:', error);
        reject(error);
      }
    });
  }
  async removeSubscriptionProductFromCart() {
    try {
      // Use /cart/update.js with variant_id to set quantity to 0
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: {
            [this.mbClubSubscriptionProductVariantId]: 0
          }
        })
      });

      if (!response.ok) {
        
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      localStorage.setItem('mb_club_minicart_disabled', 'true');
      this.isSubscriptionAdded = false;
      
      // Check if Shopify returned an error in the response
      if (data.errors) {
        throw new Error(data.errors);
      }

      return data;
    } catch (error) {
      console.error('Error in removeSubscriptionProductFromCart:', error);
      throw error;
    }
  }
  /**
   * Whether the membership is really in the cart (the cart items JSON that side-cart keeps updated).
   */
  isMembershipInCart() {
    try {
      const cartItems = JSON.parse(document.getElementById('mb-club-minicart-cart-items')?.textContent || 'null');
      return Array.isArray(cartItems)
        ? cartItems.some(item => item.product_id == this.mbClubSubscriptionProductId)
        : this.isSubscriptionAdded;
    } catch (e) {
      return this.isSubscriptionAdded;
    }
  }
  /**
   * Adds the membership automatically when "default selected" is on and the shopper hasn't opted out.
   * @returns {boolean} Whether an automatic add was started, so the render can show VIP selected right away.
   */
  evaluateIfdefaultAdded() {
    const wasDisabled = localStorage.getItem('mb_club_minicart_disabled') === 'true'; // TODO: Add a check to see if the user has already disabled the minicart
    const renderedFromLiquid = this.getAttribute('rendered-from-liquid') === 'true';
    
    // Only auto-add if cart already has at least 1 non-subscription item.
    // This prevents Elevar from tracking the auto-add as a user-initiated add-to-cart event.
    const cartItemsEl = document.getElementById('mb-club-minicart-cart-items');
    let hasRealCartItems = false;
    if (cartItemsEl) {
      try {
        const cartItems = JSON.parse(cartItemsEl.textContent);
        hasRealCartItems = Array.isArray(cartItems) && cartItems.some(
          item => item.product_id != this.mbClubSubscriptionProductId && !item.properties?._buy_now
        );
      } catch (e) {}
    }

    if (this.mbClubSubscriptionProductDefaultSelected && !this.isSubscriptionAdded && !wasDisabled && hasRealCartItems && !mbClubSubscriptionAddInFlight) {
      mbClubSubscriptionAddInFlight = true;
      this.addSubscriptionProductToCart("evaluateIfdefaultAdded")
        .then(() => {
          this.isSubscriptionAdded = true;
          this.refreshSideCart();
        })
        .catch(error => {
          mbClubSubscriptionAddInFlight = false;
          console.error('Error adding default subscription product:', error);
          // The add failed, so stop showing VIP as selected
          const onetimeRadio = this.querySelector('input[type="radio"][value="onetime"]');
          if (onetimeRadio) {
            onetimeRadio.checked = true;
            this.updateSelectedState();
          }
        });
      return true;
    }
    return false;
  }
  hideIfOnlySubscriptionItem(cartData) {
    const isOnlySubscriptionItem = cartData.items.length === 1 && this.isSubscriptionAdded;
    if (isOnlySubscriptionItem) {
      this.classList.add('hidden');
    } else {
      this.classList.remove('hidden');
    }
    this.reportVisibility();
  }
  connectedCallback() {
    // widget_membership_trigger now fires from setVipMemberStatus() itself, right when the
    // status is decided, instead of a fixed delay racing the async Intelligems check.

    // Don't render if customer is already a member
    if (window.theme?.mb_club_customer_is_member === true) {
      if (this.isSubscriptionAdded) {
        console.log('[mb-club-minicart] NOT rendering: customer is already a member, removing subscription product from cart');
        // Status update: already a member and had the subscription in cart, so we just cleaned it up.
        this.setVipMemberStatus('blocked_already_member', { subscriptionRemoved: true });
        this.removeSubscriptionProductFromCart();
      } else {
        console.log('[mb-club-minicart] NOT rendering: customer is already a member');
        // Status update: already a member, nothing to clean up.
        this.setVipMemberStatus('blocked_already_member', { subscriptionRemoved: false });
      }
      mbClubReportWidgetVisible(false);
      return;
    }
    // Don't render if customer is not in exclusive countries
    if (this.exclusiveCountries && !this.exclusiveCountries.includes(window.Shopify.country)) {
      console.log('[mb-club-minicart] NOT rendering: shopper country', window.Shopify.country, 'is not in exclusive countries list', this.exclusiveCountries);
      // Status update: shopper's country isn't on the exclusive-countries allow-list.
      this.setVipMemberStatus('blocked_country', { country: window.Shopify.country });
      mbClubReportWidgetVisible(false);
      return;
    }

    if (this.mbClubSubscriptionAllowedProductTypes?.trim()) {
      // If the widget was already activated in a previous render (qualifying product was found
      // and the subscription was added), keep showing it even if that product was later removed.
      const wasAlreadyActivated = localStorage.getItem('mb_club_widget_activated') === 'true';

      if (!wasAlreadyActivated) {
        const allowedTypes = this.mbClubSubscriptionAllowedProductTypes.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
        const cartItemsEl = document.getElementById('mb-club-minicart-cart-items');
        let hasAllowedItem = false;

        if (cartItemsEl) {
          try {
            const cartItems = JSON.parse(cartItemsEl.textContent);
            hasAllowedItem = Array.isArray(cartItems) && cartItems.some(
              item => item.product_id != this.mbClubSubscriptionProductId &&
                      allowedTypes.includes((item.product_type || '').toLowerCase())
            );
          } catch (e) {
            console.log('[mb-club-minicart] failed to parse cart items JSON, treating as no qualifying item', e);
          }
        } else {
          console.log('[mb-club-minicart] cart items script element not found (#mb-club-minicart-cart-items), treating as no qualifying item');
        }

        if (!hasAllowedItem){
          console.log('[mb-club-minicart] NOT rendering: no cart item matches allowed product types', allowedTypes);
          // Status update: cart has no item matching the allowed product types, and the widget
          // was never activated before (no sticky localStorage flag), so it's still gated.
          this.setVipMemberStatus('blocked_no_qualifying_product', { allowedTypes });
          mbClubReportWidgetVisible(false);
          return;
        } else {
          // Mark as activated so the widget persists even if the qualifying product is removed
          console.log('[mb-club-minicart] qualifying product type found, activating widget (sticky via localStorage)');
          localStorage.setItem('mb_club_widget_activated', 'true');
        }


      } else {
        console.log('[mb-club-minicart] widget already activated previously (localStorage), skipping product-type check');
      }
    }
    // If Intelligems A/B test is disabled from the dashboard, skip the test and render for everyone.
    const intelligemsPromise = this.mbClubIntelligemsEnabled
      ? mbClubMinicartShouldRenderFromIntelligems()
      : Promise.resolve(true);

    intelligemsPromise.then((shouldRender) => {
      // Control (and unknown/unassigned): do nothing. Skipped if Intelligems is disabled.
      if (this.mbClubIntelligemsEnabled) this.saveIntelligemsExperimentToLocalStorage(shouldRender);
      if (!shouldRender) {
        console.log('[mb-club-minicart] NOT rendering: Intelligems assigned this visitor to control/unassigned variant');
        // Status update: passed all cart/country/member gates, but Intelligems put this
        // visitor in control (or the experiment/test group wasn't found).
        this.setVipMemberStatus('blocked_ab_control');
        mbClubReportWidgetVisible(false);
        return;
      }
      if (!this.isConnected) {
        console.log('[mb-club-minicart] NOT rendering: element was disconnected from DOM before Intelligems promise resolved');
        // Status update: the async Intelligems check resolved after the element was
        // removed/replaced in the DOM (e.g. side-cart re-rendered), so we can't render it.
        this.setVipMemberStatus('blocked_disconnected');
        mbClubReportWidgetVisible(false);
        return;
      }

      console.log('[mb-club-minicart] rendering widget: all conditions passed');
      // Status update: every gate passed (membership, country, product type, A/B test),
      // widget is about to render.
      this.setVipMemberStatus('rendered');
      this.render().then(() => {
        this.attachEventListeners();
        const subtotal = this.getAttribute('data-subtotal');
        const originalTotalPrice = this.getAttribute('data-original-total-price');
        const cartItems = document.getElementById('mb-club-minicart-cart-items');
        const cartItemsData = JSON.parse(cartItems.textContent);
        if (subtotal && cartItemsData) {
          this.updateSubtotal(parseFloat(originalTotalPrice),parseFloat(subtotal), cartItemsData);
        }
        this.reportVisibility();
      });

    });
  }
  subscriptionSelectorEventHandler() {
    // Listen for change event on the subscription selector radio inputs
    const radioInputs = this.querySelectorAll('input[type="radio"][name="mb-club-minicart__comparison-table-row"]');
    const self = this;
    radioInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        if (e.target.value === 'onetime') {
          self.setLoadingState(true);
          self.removeSubscriptionProductFromCart()
            .then(() => {
              localStorage.setItem('mb_club_minicart_disabled', 'true');
              self.refreshSideCart();
            })
            .catch(error => {
              console.error('Error removing subscription product:', error);
              // Revert to previous selection on error
              const previousValue = self.querySelector('input[type="radio"][name="mb-club-minicart__comparison-table-row"]:checked')?.value || 'vip';
              if (previousValue === 'vip') {
                const vipRadio = self.querySelector('input[type="radio"][value="vip"]');
                if (vipRadio) vipRadio.checked = true;
              }
            })
            .finally(() => {
              self.setLoadingState(false);
            });
        } else if (e.target.value === 'vip') {
          self.setLoadingState(true);
          self.addSubscriptionProductToCart("subscriptionSelectorEventHandler")
            .then(() => {
              localStorage.removeItem('mb_club_minicart_disabled');
              self.refreshSideCart();
            })
            .catch(error => {
              console.error('Error adding subscription product:', error);
              // Revert to previous selection on error
              const previousValue = self.querySelector('input[type="radio"][name="mb-club-minicart__comparison-table-row"]:checked')?.value || 'onetime';
              if (previousValue === 'onetime') {
                const onetimeRadio = self.querySelector('input[type="radio"][value="onetime"]');
                if (onetimeRadio) onetimeRadio.checked = true;
              }
            })
            .finally(() => {
              self.setLoadingState(false);
            });
        }
      });
    });
  }

  setLoadingState(isLoading) {
    const loadingOverlay = this.querySelector('.mb-club-minicart__loading-overlay');
    const radioInputs = this.querySelectorAll('input[type="radio"][name="mb-club-minicart__comparison-table-row"]');
    const comparisonTable = this.querySelector('.mb-club-minicart__comparison-table');
    
    if (loadingOverlay) {
      loadingOverlay.classList.toggle('hidden', !isLoading);
    }
    
    radioInputs.forEach(input => {
      input.disabled = isLoading;
    });
    
    if (comparisonTable) {
      comparisonTable.style.opacity = isLoading ? '0.5' : '1';
      comparisonTable.style.pointerEvents = isLoading ? 'none' : 'auto';
    }
  }

  refreshSideCart() {
    const sideCart = document.querySelector('side-cart');
    if (sideCart && typeof sideCart.getCart === 'function') {
      sideCart.getCart();
    }
  } 
  async render() {
    const autoAddStarted = this.evaluateIfdefaultAdded();
    // VIP shows selected only when the membership is in the cart (or being added by default),
    // so with "Default selected" off the widget never looks selected without the membership.
    const isVipSelected = autoAddStarted || this.isMembershipInCart();
    const openAttribute = this.isOpen ? 'open' : '';
    this.innerHTML = `
      <div class="mb-club-minicart">
        <details class="mb-club-minicart__details" ${openAttribute}>
          <summary class="mb-club-minicart__header">
            <div class="mb-club-minicart__header-left">
              <span class="mb-club-minicart__title">M&B Club</span>
              <span class="mb-club-minicart__discount-text">VIP with 15% discount selected</span>
            </div>
            <svg class="mb-club-minicart__chevron" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </summary>
          <div class="mb-club-minicart__content">
            <div class="mb-club-minicart__loading-overlay hidden">
              <div class="mb-club-minicart__loading-spinner">
                <div></div>
              </div>              
            </div>
            <div class="mb-club-minicart__comparison">
              <div class="mb-club-minicart__comparison-table">
                <div class="mb-club-minicart__comparison-table-row description">
                <div class="mb-club-minicart__comparison-table-row-choose-container">
                  <span class="mb-club-minicart__comparison-table-row-choose">Choose one option</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <mask id="mask0_970_197" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="10" height="10">
                      <rect x="10" y="10" width="10" height="10" transform="rotate(-180 10 10)" fill="#D9D9D9"/>
                    </mask>
                    <g mask="url(#mask0_970_197)">
                      <path d="M3.5 0.999999L7.5 5L3.5 9L2.79167 8.29167L6.08333 5L2.79167 1.70833L3.5 0.999999Z" fill="#1C1B1F"/>
                    </g>
                  </svg>
                  </div>
                  <p>Members Discount</p>
                  <p>Members Store<br>Credit</p>
                  <p>Members<br>Only Sales</p>
                  <p>1st Order<br>Free Shipping</p>
                  <span class="you-pay">You Pay</span>


                </div>
                <label class="mb-club-minicart__comparison-table-row mb-club-minicart__comparison-table-row--vip">
                  
                  <div class="mb-club-minicart__option-content">
                    <input type="radio" name="mb-club-minicart__comparison-table-row" value="vip" class="mb-club-minicart__radio" ${isVipSelected ? 'checked' : ''}>
                    <span class="mb-club-minicart__radio-custom mb-club-minicart__radio-custom--checked"></span>
                    <span class="mb-club-minicart__comparison-table-row-label">VIP</span>
                   
                  </div>
                  <div class="mb-club-minicart__comparison-table-cell mb-club-minicart__comparison-table-cell--vip">
                    <span class="mb-club-minicart__benefit-value">${this.mbClubDiscountPercent}%</span>
                  </div>
                  <div class="mb-club-minicart__comparison-table-cell mb-club-minicart__comparison-table-cell--vip">
                    <span class="mb-club-minicart__benefit-value">$40</span>
                  </div>
                  <div class="mb-club-minicart__comparison-table-cell mb-club-minicart__comparison-table-cell--vip">
                    <span class="mb-club-minicart__check">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <mask id="mask0_970_188" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
                          <rect width="20" height="20" fill="#D9D9D9"/>
                        </mask>
                        <g mask="url(#mask0_970_188)">
                          <path d="M8.9375 13L13.8958 8.0625L12.8333 7L8.9375 10.875L7.16667 9.125L6.10417 10.1875L8.9375 13ZM10 18C8.90278 18 7.86806 17.7917 6.89583 17.375C5.92361 16.9583 5.07292 16.3854 4.34375 15.6562C3.61458 14.9271 3.04167 14.0764 2.625 13.1042C2.20833 12.1319 2 11.0972 2 10C2 8.88889 2.20833 7.85069 2.625 6.88542C3.04167 5.92014 3.61458 5.07292 4.34375 4.34375C5.07292 3.61458 5.92361 3.04167 6.89583 2.625C7.86806 2.20833 8.90278 2 10 2C11.1111 2 12.1493 2.20833 13.1146 2.625C14.0799 3.04167 14.9271 3.61458 15.6562 4.34375C16.3854 5.07292 16.9583 5.92014 17.375 6.88542C17.7917 7.85069 18 8.88889 18 10C18 11.0972 17.7917 12.1319 17.375 13.1042C16.9583 14.0764 16.3854 14.9271 15.6562 15.6562C14.9271 16.3854 14.0799 16.9583 13.1146 17.375C12.1493 17.7917 11.1111 18 10 18ZM10 16.5C11.8056 16.5 13.3403 15.8681 14.6042 14.6042C15.8681 13.3403 16.5 11.8056 16.5 10C16.5 8.19444 15.8681 6.65972 14.6042 5.39583C13.3403 4.13194 11.8056 3.5 10 3.5C8.19444 3.5 6.65972 4.13194 5.39583 5.39583C4.13194 6.65972 3.5 8.19444 3.5 10C3.5 11.8056 4.13194 13.3403 5.39583 14.6042C6.65972 15.8681 8.19444 16.5 10 16.5Z" fill="#206203"/>
                        </g>
                      </svg>
                    </span>
                  </div>
                  <div class="mb-club-minicart__comparison-table-cell mb-club-minicart__comparison-table-cell--vip">
                    <span class="mb-club-minicart__check">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <mask id="mask0_970_189" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
                          <rect width="20" height="20" fill="#D9D9D9"/>
                        </mask>
                        <g mask="url(#mask0_970_189)">
                          <path d="M8.9375 13L13.8958 8.0625L12.8333 7L8.9375 10.875L7.16667 9.125L6.10417 10.1875L8.9375 13ZM10 18C8.90278 18 7.86806 17.7917 6.89583 17.375C5.92361 16.9583 5.07292 16.3854 4.34375 15.6562C3.61458 14.9271 3.04167 14.0764 2.625 13.1042C2.20833 12.1319 2 11.0972 2 10C2 8.88889 2.20833 7.85069 2.625 6.88542C3.04167 5.92014 3.61458 5.07292 4.34375 4.34375C5.07292 3.61458 5.92361 3.04167 6.89583 2.625C7.86806 2.20833 8.90278 2 10 2C11.1111 2 12.1493 2.20833 13.1146 2.625C14.0799 3.04167 14.9271 3.61458 15.6562 4.34375C16.3854 5.07292 16.9583 5.92014 17.375 6.88542C17.7917 7.85069 18 8.88889 18 10C18 11.0972 17.7917 12.1319 17.375 13.1042C16.9583 14.0764 16.3854 14.9271 15.6562 15.6562C14.9271 16.3854 14.0799 16.9583 13.1146 17.375C12.1493 17.7917 11.1111 18 10 18ZM10 16.5C11.8056 16.5 13.3403 15.8681 14.6042 14.6042C15.8681 13.3403 16.5 11.8056 16.5 10C16.5 8.19444 15.8681 6.65972 14.6042 5.39583C13.3403 4.13194 11.8056 3.5 10 3.5C8.19444 3.5 6.65972 4.13194 5.39583 5.39583C4.13194 6.65972 3.5 8.19444 3.5 10C3.5 11.8056 4.13194 13.3403 5.39583 14.6042C6.65972 15.8681 8.19444 16.5 10 16.5Z" fill="#206203"/>
                        </g>
                      </svg>
                    </span>
                  </div>
                  <div class="mb-club-minicart__comparison-table-cell mb-club-minicart__comparison-table-cell--vip">
                    <div class="mb-club-minicart__price-button mb-club-minicart__price-button--vip"></div>
                  </div>
                </label>
                <label class="mb-club-minicart__comparison-table-row mb-club-minicart__comparison-table-row--onetime">
                  
                  <div class="mb-club-minicart__option-content">
                    <input type="radio" name="mb-club-minicart__comparison-table-row" value="onetime" class="mb-club-minicart__radio" ${isVipSelected ? '' : 'checked'}>
                    <span class="mb-club-minicart__radio-custom"></span>
                    <span class="mb-club-minicart__comparison-table-row-label">One-time</span>
                  </div>
                  <div class="mb-club-minicart__comparison-table-cell mb-club-minicart__comparison-table-cell--onetime">
                    <span class="mb-club-minicart__cross">✕</span>
                  </div>
                  <div class="mb-club-minicart__comparison-table-cell mb-club-minicart__comparison-table-cell--onetime">
                    <span class="mb-club-minicart__cross">✕</span>
                  </div>
                  <div class="mb-club-minicart__comparison-table-cell mb-club-minicart__comparison-table-cell--onetime">
                    <span class="mb-club-minicart__cross">✕</span>
                  </div>
                  <div class="mb-club-minicart__comparison-table-cell mb-club-minicart__comparison-table-cell--onetime">
                    <span class="mb-club-minicart__cross">✕</span>
                  </div>
                  <div class="mb-club-minicart__comparison-table-cell mb-club-minicart__comparison-table-cell--onetime">
                    <div class="mb-club-minicart__price-button mb-club-minicart__price-button--onetime"></div>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </details>
        <div class="mb-club-minicart__description">
         
          <p>By completing your purchase, you’ll be enrolled in the Mott & Bow VIP Club. Membership is $${this.mbClubSubscriptionProductPrice } and is returned to you each month as store credit. Your first month is complimentary, with the flexibility to pause or cancel at any time. Plus, enjoy free shipping on your first order—on us.</p>
        </div>
      </div>
    `;
    this.subscriptionSelectorEventHandler();

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'mb_club_widget_viewed',
      mb_club_subscription_added: this.isSubscriptionAdded,
    });

    return true;
  }

  attachEventListeners() {
    const details = this.querySelector('.mb-club-minicart__details');
    const chevron = this.querySelector('.mb-club-minicart__chevron');
    
    if (details) {
      // Set initial chevron rotation based on initial open state
      if (chevron) {
        chevron.style.transform = details.open ? 'rotate(180deg)' : 'rotate(0deg)';
      }
      
      details.addEventListener('toggle', () => {
        this.isOpen = details.open;
        // Save the open/closed state to localStorage
        localStorage.setItem('mb_club_minicart_open', this.isOpen ? 'true' : 'false');
        if (chevron) {
          chevron.style.transform = this.isOpen ? 'rotate(180deg)' : 'rotate(0deg)';
        }
      });
    }

    // Handle radio button changes
    const radios = this.querySelectorAll('.mb-club-minicart__radio');
    radios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        this.handleOptionChange(e.target.value);
        this.updateSelectedState();
      });
    });


    // Initial state update
    this.updateSelectedState();
  }

  updateSelectedState() {
    const radios = this.querySelectorAll('.mb-club-minicart__radio');
    const vipRow = this.querySelector('.mb-club-minicart__comparison-table-row--vip');
    const onetimeRow = this.querySelector('.mb-club-minicart__comparison-table-row--onetime');
    
    radios.forEach(radio => {
      const customRadio = radio.nextElementSibling; // The custom radio span
      if (radio.checked) {
        if (customRadio) customRadio.classList.add('mb-club-minicart__radio-custom--checked');
        if (radio.value === 'vip' && vipRow) vipRow.classList.add('mb-club-minicart__comparison-table-row--selected');
        if (radio.value === 'onetime' && onetimeRow) onetimeRow.classList.add('mb-club-minicart__comparison-table-row--selected');
      } else {
        if (customRadio) customRadio.classList.remove('mb-club-minicart__radio-custom--checked');
        if (radio.value === 'vip' && vipRow) vipRow.classList.remove('mb-club-minicart__comparison-table-row--selected');
        if (radio.value === 'onetime' && onetimeRow) onetimeRow.classList.remove('mb-club-minicart__comparison-table-row--selected');
      }
    });
  }

  handleOptionChange(value) {
    // Handle option change logic here
    console.log('Selected option:', value);
  }

  /**
   * Update the subtotal price displayed in the onetime button
   * @param {number} subtotal - The cart subtotal in dollars
   * @param {Array} cartItemsData - Array of cart items
   */
  updateSubtotal(subtotalBeforeDiscount,subTotal, cartItemsData ) {
    if (subTotal == null || subTotal == undefined || subTotal == 0 || subTotal == '0') {
      this.classList.add('hidden');
      this.reportVisibility();
      return;
    } else {
      this.classList.remove('hidden'); 
    }

    //this.isSubscriptionAdded = cartItemsData.some(item => item.product_id == this.mbClubSubscriptionProductId);
    const onetimeButton = this.querySelector('.mb-club-minicart__price-button--onetime');
    const vipButton = this.querySelector('.mb-club-minicart__price-button--vip');

    let isSubscriptionAddedFromCartItems = cartItemsData.some(item => item.product_id == this.mbClubSubscriptionProductId);
    
    if (onetimeButton && subtotalBeforeDiscount !== null && subtotalBeforeDiscount !== undefined) {
      let displaySubtotalBeforeDiscount = subtotalBeforeDiscount;

      // If subscription is added, subtract the subscription price from the subtotalBeforeDiscount
      if (this.isSubscriptionAdded || isSubscriptionAddedFromCartItems) {
          // line_price is in cents, convert to dollars
          displaySubtotalBeforeDiscount = subtotalBeforeDiscount - this.mbClubSubscriptionProductPrice;
        
      } else {
        // If subscription is not added, just use the simple subtotalBeforeDiscount
        displaySubtotalBeforeDiscount = subtotalBeforeDiscount;
      }
      
      // Format the price using Shopify's formatMoneyAmmount if available, otherwise use toFixed
      const formattedPrice = window.Shopify && window.Shopify.formatMoneyAmmount 
        ? window.Shopify.formatMoneyAmmount(displaySubtotalBeforeDiscount)
        : displaySubtotalBeforeDiscount.toFixed(2);
      const currencySymbol = window.theme?.cart_currency_symbol || '$';
      onetimeButton.textContent = `${currencySymbol}${formattedPrice}`;
      this.onetimeButtonText = `${currencySymbol}${formattedPrice}`;
    }
    if(vipButton && subTotal !== null && subTotal !== undefined) {
      if(this.isSubscriptionAdded || isSubscriptionAddedFromCartItems) {
      // Only update the vipButton with the cart subtotal (subTotal, which is expected to be in dollars)
      const formattedVipPrice = window.Shopify && window.Shopify.formatMoneyAmmount 
        ? window.Shopify.formatMoneyAmmount(subTotal)
        : subTotal.toFixed(2);
      const currencySymbol = window.theme?.cart_currency_symbol || '$';
      vipButton.textContent = `${currencySymbol}${formattedVipPrice}`;
      } else {
        const clubDiscountSubtotal = parseFloat(this.getAttribute('data-club-discount-subtotal'));
        const totalPlusSubscriptionPrice = !isNaN(clubDiscountSubtotal) ? clubDiscountSubtotal : subTotal;
        const formattedVipPrice = window.Shopify && window.Shopify.formatMoneyAmmount
          ? window.Shopify.formatMoneyAmmount(totalPlusSubscriptionPrice)
          : Number(totalPlusSubscriptionPrice);
        const currencySymbol = window.theme?.cart_currency_symbol || '$';
        vipButton.textContent = `${currencySymbol}${formattedVipPrice}`;
        this.vipButtonText = `${currencySymbol}${formattedVipPrice}`;
      }
    }
  }
  roundHalfUp(num, decimals = 2) {
  // Round to 1 decimal first (Shopify's intermediate step)
  const intermediate = Math.round(num * 10) / 10;
  // Then round to final decimals
  const multiplier = Math.pow(10, decimals);
  return Math.round(intermediate * multiplier) / multiplier;
}

  /**
   * Observe attribute changes for data-subtotal
   */
  static get observedAttributes() {
    return ['data-subtotal'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-subtotal' && newValue !== oldValue) {
      const subtotal = parseFloat(newValue);
      if (!isNaN(subtotal)) {
        // Get cart items data from the script tag
        const cartItems = document.getElementById('mb-club-minicart-cart-items');
        const originalTotalPrice = this.getAttribute('data-original-total-price');
        const cartItemsData = cartItems ? JSON.parse(cartItems.textContent) : null;
        
        this.updateSubtotal(originalTotalPrice,subtotal, cartItemsData);
      }
    }
  }
}

function mbClubDefineMinicart() {
  window.mbClubReportWidgetVisible = mbClubReportWidgetVisible;
  customElements.define("mb-club-minicart", MBClubMinicart);
}
// M&B Club A/B test: only the widget picked for this visitor is defined (mb-club-experiment.js).
if (window.mbClubExperiment) {
  window.mbClubExperiment.whenWidget('v1', mbClubDefineMinicart);
} else {
  mbClubDefineMinicart();
}
